import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { MailLoginCard, MailLoginForm, MailLoginOtpForm } from '../../../components/mail'
import { MailOTPMethod } from '../../../components/mail/login/MailLoginForm/MailLoginForm.model'
import {
  MailWebServiceLoginFormFields,
  MailWebServiceLoginOtpFormFields,
} from '../../../model/Mail'
import { isDoctor } from '../../../misc/user.utilities'
import { MailNoAddresses } from '../../../components/mail/login/MailNoAddresses'
import {
  useFinalizeMailAuthentication,
  useGetMailAdresses,
  useInitiateMailAuthentication,
} from '../../../hooks/queries/mail'
import { useAtom } from 'jotai'
import {
  mailLoginRedirect,
  mailWebserviceAuthFirstStepToken,
  mailWebserviceAuthToken,
} from '../../../state/mail'
import { useNavigate } from 'react-router-dom'
import { RESET } from 'jotai/utils'
import { useUserEnabledFeature } from '../../../hooks/utils/user'
import { FeaturesType } from '@follow/cdk'
import { ConnectedUserContext } from '../../../misc/auth.utilities'
import { LoadingPage } from '../../../components/shared'
import { selectedEmailAddressAtom } from '../../../state/mail/selectedEmailAddress.atom'
import { useQueryClient } from '@tanstack/react-query'
import { mailWebserviceKeys } from '../../../hooks/queries/mail/webservice/webservice.keys'
import styles from './MailLoginPage.module.scss'

export const MailLoginPage: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const [otpMethod, setOtpMethod] = useState(MailOTPMethod.SMS)

  const navigate = useNavigate()
  const [redirectAfterLogin, setRedirectAfterLogin] = useAtom(mailLoginRedirect)
  const [webserviceToken, setWebserviceToken] = useAtom(mailWebserviceAuthToken)
  const [webserviceFirstStepToken, setWebserviceFirstStepToken] = useAtom(
    mailWebserviceAuthFirstStepToken,
  )

  const { mutate: initiateAuthentication } = useInitiateMailAuthentication()
  const { mutate: finalizeAuthentication } = useFinalizeMailAuthentication()
  const {
    query: { isFetching: isMailAddressListLoading },
  } = useGetMailAdresses({ enabled: !!webserviceToken })
  const [selectedEmailAddress, setSelectedEmailAddress] = useAtom(selectedEmailAddressAtom)

  const { currentUser } = useContext(ConnectedUserContext)
  const isMssSmtpActive = useUserEnabledFeature(FeaturesType.mssSmtp)

  function handleLogin(values: MailWebServiceLoginFormFields) {
    setSelectedEmailAddress(null)
    queryClient.removeQueries({ queryKey: mailWebserviceKeys.mailAddresses })

    setWebserviceToken(null)
    setWebserviceFirstStepToken(null)

    initiateAuthentication(values, {
      onSuccess: (data) => {
        setOtpMethod(values.otpMethod)
        setWebserviceFirstStepToken(data.authenticationId)
      },
    })
  }

  function handleLoginOtp({ otpPassword }: MailWebServiceLoginOtpFormFields) {
    if (otpPassword && webserviceFirstStepToken) {
      finalizeAuthentication(
        { otpPassword, tmpAuthenticationId: webserviceFirstStepToken },
        {
          onSettled: () => {
            setWebserviceFirstStepToken(null)
          },
        },
      )
    }
  }

  useEffect(() => {
    if (webserviceToken && selectedEmailAddress) {
      const redirect = redirectAfterLogin
      setRedirectAfterLogin(RESET)
      navigate(redirect)
    }
  }, [navigate, redirectAfterLogin, selectedEmailAddress, setRedirectAfterLogin, webserviceToken])

  function handleRetry() {
    setWebserviceFirstStepToken('')
  }

  return (
    <div className={styles.mailLoginPageContainer}>
      {isMssSmtpActive && isDoctor(currentUser) && !currentUser.mailizEmailAddress ? (
        <MailLoginCard>
          <MailNoAddresses />
        </MailLoginCard>
      ) : (
        <>
          {isMailAddressListLoading && !selectedEmailAddress ? (
            <LoadingPage />
          ) : (
            <MailLoginCard>
              {!webserviceFirstStepToken ? (
                <MailLoginForm onSubmit={handleLogin} currentUser={currentUser} />
              ) : (
                <MailLoginOtpForm
                  onSubmit={handleLoginOtp}
                  onRetry={handleRetry}
                  otpMethod={otpMethod}
                  firstStepToken={webserviceFirstStepToken}
                />
              )}
            </MailLoginCard>
          )}
        </>
      )}
    </div>
  )
}
